import { Colors } from "@/enums/Colors";
import { Spinner } from "@/external/ensolvers-core-frontend-mui-v5/components/Common/Spinner";
import environment from "@/external/ensolvers-core-frontend-mui-v5/submodules/fox-typescript/environment.json";
import { resolveLoginRedirect } from "@/utils/LoginRedirectTarget";
import { useEffect } from "react";
import { useSearchParams } from "react-router-dom";

/**
 * `/new-login` — entry point for the standalone auth service (lazo-x LAZ-2358).
 * This SPA is a static export in QA/prod, so the path is a client-side
 * redirect to the auth/app sign-in rather than a server rewrite. Works on
 * every host this bundle serves (app.lazo.one, qa.lazo.one, workspace slugs).
 *
 * The destination after sign-in defaults to this origin; an explicit
 * `?returnTo=` is forwarded verbatim — auth/app validates it against its
 * own *.lazo.one allow-list before honoring it (open-redirect defense lives
 * there, where the redirect is performed).
 */
export interface NewLoginProps {
  /** Test seam. When provided, used in place of `window.location.replace`. */
  navigate?: (url: string) => void;
  /** Test seam. When provided, used in place of `window.location.origin`. */
  currentOrigin?: string;
  /** Test seam. When provided, used in place of `window.location.host`. */
  currentHost?: string;
}

export const NewLogin = ({ navigate, currentOrigin, currentHost }: NewLoginProps) => {
  const [searchParams] = useSearchParams();

  useEffect(() => {
    // An explicit `?returnTo=` wins, but is not trusted to name a real destination: arriving at
    // `/login?returnTo=https://app.lazo.one/` is as destination-less as arriving at `/login`, and
    // resolveLoginRedirect drops both. Without the param the fallback is this origin — a bare root,
    // which it also drops.
    const dest = searchParams.get("returnTo") ?? currentOrigin ?? window.location.origin;
    // This route only exists to hand off to the auth app, so the flag is already decided by the
    // time anyone lands here.
    const target = resolveLoginRedirect({
      newLoginEnabled: true,
      host: currentHost ?? window.location.host,
      env: environment.env,
      currentUrl: dest,
    });
    if (navigate) {
      navigate(target);
      return;
    }
    window.location.replace(target);
  }, [searchParams, navigate, currentOrigin, currentHost]);

  return <Spinner sx={{ color: Colors.LAZO_PURPLE }} />;
};
